import { Component, OnInit, OnDestroy, AfterViewInit, Input, Output, EventEmitter, ChangeDetectorRef, ViewChild, ElementRef, TemplateRef, EmbeddedViewRef } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { Overlay, OverlayRef, OverlayConfig } from '@angular/cdk/overlay';
import { TemplatePortalDirective } from '@angular/cdk/portal';
import { MatDialog, MatDialogRef } from '@angular/material';

import { FieldComponent, UndoAction } from './field.component';
import { FormField, Disclaimer, Link } from './form-field.model';
import { AutocompleteComponent } from './autocomplete.component';
import { SelectDialogComponent } from './select-dialog.component';
import { DynamicOverlaysDirective } from './dynamic-overlays.directive';

interface FieldValue {
  displayed?: string,
  stored?: string,
  categories?: string,
  linked?: string,
  linked_categories?: string[],
  description?: string
}

@Component({
  selector: 'app-autocomplete-select-field',
  templateUrl: './autocomplete-select-field.component.html',
  styleUrls: ['./autocomplete-select-field.component.scss']
})
export class AutocompleteSelectFieldComponent extends FieldComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() draftData: any;
  @Input() dynamicOverlays: DynamicOverlaysDirective;
  @Input() field: FormField;
  @Input() locked: boolean = false;
  @Input() lockValues: boolean = false;
  @Input() templateData: any;
  @Input() workflowId: number;
  @Output() disclaimerChanged = new EventEmitter<{field: string, value: any}>();
  @Output() focusChanged = new EventEmitter<boolean>();
  @Output() highlightChanged = new EventEmitter<{highlighted: boolean, color?: string}>();
  @Output() resized = new EventEmitter<FieldComponent>();
  @Output() returnKey = new EventEmitter<FieldComponent>();
  @Output() showDisclaimer = new EventEmitter<{field: string, disclaimer: Disclaimer}>();
  @Output() undoableAction = new EventEmitter<UndoAction>();
  @Output() updateLinked = new EventEmitter<{link: Link, value?: any, categories?: any[], enabled?: boolean}>();
  @Output() viewLoaded = new EventEmitter<FieldComponent>();
  @ViewChild("fieldElement") fieldElement: ElementRef;
  @ViewChild("autocompleteTemplate") autocompleteTemplate: TemplatePortalDirective;
  @ViewChild("autocomplete") autocomplete: AutocompleteComponent;
  @ViewChild("versionTemplate") versionTemplate: TemplateRef<null>;
  @ViewChild("versionElement") versionElement: ElementRef;

  active: boolean = false;
  categories: string[];
  debounce: number = 0;
  defaultValue: FieldValue;
  disclaimer: Disclaimer;
  disclaimers: Disclaimer[];
  focused: boolean = false;
  hasInputDialog: boolean = false;
  highlightColor: string;
  highlightPattern: string;
  highlightValues: string[];
  highlighted: boolean = false;
  id: string;
  label: string;
  links: Link[];
  loading: boolean = false;
  overlayRef: OverlayRef;
  placeholder: string;
  previousValue: FieldValue;
  required: boolean = false;
  matchRegex: RegExp;
  selectedIndex: number;
  showingPreviousValue: boolean = false;
  suggestions: FieldValue[] = [];
  touched: boolean = false;
  valid: boolean = false;
  value: FieldValue;
  values: FieldValue[];
  versionChanged: boolean = false;
  versionView: EmbeddedViewRef<null>;
  get invalid(): boolean { return !this.valid && !this.active && (!this.blank || this.touched) }
  get controlIsBlank(): boolean {
    return this.control == null ||
    this.control.value == null ||
    this.control.value.trim() === ''
  }

  constructor(
    cd: ChangeDetectorRef,
    private dialog: MatDialog,
    private overlay: Overlay) { super(cd) }

  ngOnInit() {
    if (this.id == null) {this.id = this.field.id}
    if (this.placeholder == null) {this.placeholder = this.field.placeholder}
    if (this.values == null) {this.values = this.field.values || []}
    if (this.values.length > 0 && this.values[0].hasOwnProperty('description')) {this.hasInputDialog = true};
    this.label = this.field.label;
    this.required = this.field.required;
    this.links = this.field.links;
    this.required = this.field.required;
    this.highlightColor = this.field.highlightColor;
    this.highlightPattern = this.field.highlightPattern;
    this.highlightValues = this.field.highlightValues;
    if (this.field.locked) { this.locked = true }
    this.disclaimers = this.field.disclaimers;
    this.dropdownWidth = this.field.dropdownWidth;

    this.loadDefaultValue();
    this.loadInitialState(this.draftData);
  }

  ngOnDestroy() {
    this.hidePreviousValue();
  }

  ngAfterViewInit() {
    this.viewLoaded.emit(this);
  }

  loadTemplateValue(value) {
    return (value instanceof Array) ? value[0] : value;
  }

  loadDefaultValue() {
    let templateValue: FieldValue;
    if (this.templateData != null) {
      templateValue = this.loadTemplateValue(this.templateData);
    }

    if (templateValue != null) {
      this.defaultValue = {
        stored: templateValue.stored,
        displayed: templateValue.displayed
      };
      if (this.lockValues) { this.locked = true };
    } else if (this.field.defaultValue != null) {
      let fieldValue = this.field.defaultValue;
      this.defaultValue = {
        stored: fieldValue.stored,
        displayed: fieldValue.displayed
      }
    }
  }

  loadInitialState(draftValue?: FieldValue) {
    this._dirty = false;
    this.touched = false;

    this.value = draftValue != null ? this.loadTemplateValue(draftValue) : this.defaultValue;
    let controlValue = this.value != null ? this.value.displayed : null;
    this.control = new FormControl({value: controlValue, disabled: this.locked}, []);
    this.control.valueChanges.subscribe(
      (value) => {
        if (!(this.locked || this.lockValues)) {
          this.value = null;
          this.validate();
          this.emitValue();
        }
      }
    )
    this.control.valueChanges.debounceTime(this.debounce).subscribe(
      (value) => {
        if (this.focused) { this.onValueChange(value) }
      }
    )

    this.validate();
    this.emitValue();
    this.initialized = true;
  }

  onValueChange(value) {
    this.active = true;
    this.clearSuggestions();
    this._dirty = true;
    this.value = null;
    this.validate();

    this.setMatchRegex(value);
    
    this.loading = true;
    this.cd.markForCheck();
    this.loadSuggestions(value);
  }

  setMatchRegex(value) {
    if (value != null && value.trim() !== '')  {
      let words = value.split(/\W+/).filter((w) => w !== '');
      this.matchRegex = new RegExp('(' + words.map((w) => '\\b' + w.regExpEscape()).join("|") + ')', 'gi');
    } else {
      this.matchRegex = null;
    }
  }

  loadSuggestions(value) {
    if (value == null || value.trim() === '') {
      this.suggestions = this.categoryValues(this.values);
    } else {
      let words = value.split(/\W+/).filter((w) => w !== '');
      let regexes = words.map((w) => new RegExp("\\b" + w.regExpEscape(), 'i'));
      let categoryValues = this.categoryValues(this.values);
      let filteredValues = categoryValues.filter((v) => regexes.filter((r) => !r.test(v.displayed)).length === 0);
      if (categoryValues.length <= 8) {
        this.suggestions = categoryValues;
        if (filteredValues.length > 0) {
          this.selectedIndex = categoryValues.indexOf(filteredValues[0]);
        }
      } else {
        this.suggestions = filteredValues;
        if (filteredValues.length > 0) {
          this.selectedIndex = 0;
        }
      }
    }
    if (this.suggestions.length > 0) {
      this.showSuggestions();
    }
    this.loading = false;
    this.cd.markForCheck();
  }

  clearSuggestions() {
    this.suggestions = [];
    this.selectedIndex = null;
    if (this.overlayRef != null) {this.overlayRef.dispose()};
  }

  showSuggestions() {
    if (!this.focused) { return }
    let config = new OverlayConfig();
    config.positionStrategy = this.overlay.position().connectedTo(
      this.fieldElement,
      {originX: 'center', originY: 'bottom'},
      {overlayX: 'center', overlayY: 'top'}
    );
    config.scrollStrategy = this.overlay.scrollStrategies.reposition();
    config.width = this.dropdownWidth != null ? this.dropdownWidth : this.fieldElement.nativeElement.offsetWidth;
    if (this.overlayRef != null) {this.overlayRef.dispose()};
    this.overlayRef = this.overlay.create(config);
    this.overlayRef.attach(this.autocompleteTemplate);
  }

  commitSelected() {
    let index = this.autocomplete != null ? this.autocomplete.selectedIndex : null;
    if (index != null) {
      let suggestion = this.suggestions[index];
      if (this.value == null || this.value.stored !== suggestion.stored) {
        this.value = suggestion;
        let controlValue = this.value != null ? this.value.displayed : '';
        this.control.setValue(controlValue, {emitEvent: false});
        this.updateLinkedFields(true);
      }
    } else if (this.value != null && this.control.value !== this.value.displayed) {
      this.value = null;
    }
    this.clearSuggestions();
    this.validate();
    this.emitValue();
    this.updateLinkedRows();
  }

  autoSelected(index) {
    this.commitSelected();
  }

  validate() {
    this.valid = this.locked == true || this.value != null ||
      (!this.required && this.controlIsBlank);
  }

  emitValue() {
    let value = this.value != null ? {
      displayed: this.value.displayed,
      stored: this.value.stored
    } : null
    this.valueChanged.emit({field: this.id, value: value});
    this.checkDisclaimers();
    this.checkHighlight();
  }

  setValue(value: any, emit: boolean = true) {
    this.value = value;
    let controlValue = value != null ? value.displayed : '';
    this.control.setValue(controlValue, {emitEvent: false});
    this.validate();
    if (emit) {
      this.emitValue();
      this.updateLinkedFields();
    }
  }

  setPreviousValue(value) {
    this.previousValue = value;
    this.versionChanged = !this.valuesEqual(this.value, this.previousValue);
    if (this.versionChanged) {
      this.showPreviousValue();
    }
    this.cd.markForCheck();
  }

  resetPreviousValue() {
    this.previousValue = null;
    this.versionChanged = false;
    this.hidePreviousValue();
    this.cd.markForCheck();
  }

  onFocus(event) {
    this.focused = true;
    this.matchRegex = null;
    this.loadSuggestions(null);
    this.focusChanged.emit(true);
    return true;
  }

  onBlur(event) {
    this.focused = false;
    this.active = false;
    this.touched = true;
    this.clearSuggestions();
    this.focusChanged.emit(false);
    return true;
  }

  onKey(event) {
    switch (event.keyCode) {
    case 9:
      return this.onTabKey();
    case 13:
      return this.onReturnKey();
    case 38:
      return this.onUpArrowKey();
    case 40:
      return this.onDownArrowKey();
    default: 
      return true;
    }
  }

  onTabKey() {
    this.commitSelected();
    return true;
  }

  onReturnKey() {
    this.commitSelected();
    this.focusTabbed.emit(this);
    return false;
  }

  onUpArrowKey() {
    if (this.autocomplete != null) {
      this.autocomplete.selectPrevious();
    }
    return false;
  }

  onDownArrowKey() {
    if (this.autocomplete != null) {
      this.autocomplete.selectNext();
    }
    return false;
  }

  reset() {
    this.setValue(this.defaultValue);
    this._dirty = false;
    this.touched = false;
    if (this.control != null) {
      this.control.markAsPristine();
    }
  }

  compare(other): number {
    let a = this.value != null && this.value.displayed != null ? this.value.displayed : '';
    let b = other.value != null && other.value.displayed != null ? other.value.displayed : '';
    if (a === '') {
      return 1;
    } else if (b === '') {
      return -1;
    } else {
      return a.localeCompare(b, 'us', {sensitivity: "base", ignorePunctuation: true});
    }
  }

  valuesEqual(a,b): boolean {
    a = a != null ? a.stored : null;
    b = b != null ? b.stored : null;
    return a === b;
  }

  checkDisclaimers() {
    if (this.disclaimers != null) {
      let value = this.value != null ? this.value.stored : null;
      this.disclaimer = this.disclaimers.find((d) => d.value === value);
      this.disclaimerChanged.emit({field: this.id, value: this.disclaimer});
    }
  }

  onShowDisclaimer(event) {
    event.stopPropagation();
    this.showDisclaimer.emit({field: this.id, disclaimer: this.disclaimer})
  }
  
  updateLinkedFields(userInitiated: boolean = false) {
    if (this.links != null) {
      for (let link of this.links) {
        if (link.row) { continue }
        if (this.values.length > 0 && this.values[0].hasOwnProperty('linked')) {
          if (userInitiated) {
            let value = this.value != null ? this.value.linked : null;
            this.updateLinked.emit({link: link, value: value})
          }
        } else if (this.values.length > 0 && this.values[0].hasOwnProperty('linked_categories')) {
          let categories = this.value != null ? this.value.linked_categories : null;
          this.updateLinked.emit({link: link, categories: categories})
        } else {
          this.updateLinked.emit({link: link, value: this.value})
        }
      }
    }
  }

  updateLinkedRows() {
    if (this.links != null) {
      for (let link of this.links) {
        if (link.row) {
          let enabled = false;
          if (link.presence) {
            enabled = (this.value != null)
          } else if (link.match && this.value != null) {
            enabled = (link.match.find((x) => x == this.value.stored) != null)
          }
          this.updateLinked.emit({link: link, enabled: enabled})
        }
      }
    }
  }

  setCategories(categories) {
    this.categories = categories;
    if (this.value != null) {
      let values = this.categoryValues(this.values);
      if (values.find((v) => v.stored === this.value.stored) == null) {
        this.setValue(null);
      }
    }
  }

  categoryValues(values: FieldValue[]): FieldValue[] {
    if (this.categories != null) {
      return values.filter((v) => {
        if (v.categories == null) {return true};
        for (let category of this.categories) {
          for (let vc of v.categories) {
            if (vc === category) { return true }
          }
        }
        return false;
      })
    } else {
      return values;
    }
  }

  showInputDialog(event) {
    event.stopPropagation();
    let values = this.categoryValues(this.values)
    let dialogRef = this.dialog.open(SelectDialogComponent, {
      height: Math.min(window.innerHeight, 582)+"px",
      width: Math.min(window.innerWidth, 600)+"px",
      data: {label: this.label, values: values}
    });
    dialogRef.afterClosed().subscribe((value) => {
      if (value != null) {
        this.setValue(value);
      }
      this.focusTabbed.emit(this);
    })
  }

  checkHighlight() {
    let value = this.value != null ? this.value.stored : '';
    if (this.highlightValues != null) {
      let match = false;
      for (let v of this.highlightValues) {
        if (v === value) {
          match = true;
          break;
        }
      }
      this.highlighted = match;
      this.highlightChanged.emit({highlighted: this.highlighted});
    } else if (this.highlightPattern != null) {
      let regex = new RegExp(this.highlightPattern);
      this.highlighted = regex.test(value);
      this.highlightChanged.emit({highlighted: this.highlighted});
    }
  } 

  showPreviousValue() {
    this.showingPreviousValue = true;
    let container = this.dynamicOverlays.viewContainerRef;
    this.versionView = container.createEmbeddedView(this.versionTemplate);
    this.positionOverlays();
  }

  hidePreviousValue() {
    if (this.versionView != null) {
      this.versionView.destroy();
    }
    this.showingPreviousValue = false;
  }

  togglePreviousValue() {
    if (this.showingPreviousValue) {
      this.hidePreviousValue();
    } else {
      this.showPreviousValue();
    }
  }

  positionOverlays() {
    if (this.versionView != null) {
      let rect = this.versionElement.nativeElement.getBoundingClientRect();
      let rootNode = this.versionView.rootNodes.find((n) => n.tagName == 'DIV');
      rootNode.style.left = '' + (rect.left + window.pageXOffset) + 'px';
      rootNode.style.top = '' + (rect.top + window.pageYOffset) + 'px';
      rootNode.style.maxWidth = '' + rect.width + 'px';
    }
  }

  setPatternValue(field: string, value: any) { }

  undo(action: UndoAction) { }
}


// WEBPACK FOOTER //
// ./src/app/forms/autocomplete-select-field.component.ts